import { CDN2 } from '@/helpers/api';
import { setLastPageVisited } from '@helpers/history';
import { isServer } from '@utils/isServer';
import { Layout } from 'antd';
import { observer } from 'mobx-react';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import Router from 'next/router';
import React, { useEffect } from 'react';

declare const window: any;

const { Content } = Layout;
const isProdution = process.env.NODE_ENV === 'production';

const NavBar = dynamic(() => import('@/components2/NavBar'), {
  ssr: true,
});
const AntFooter = dynamic(() => import('@/components2/Footer'), {
  ssr: true,
});

function FacebookPixel() {
  useEffect(() => {
    import('react-facebook-pixel')
      .then((x) => x.default)
      .then((ReactPixel) => {
        window.ReactPixel = ReactPixel;
        ReactPixel.init('522092471979398');
        ReactPixel.pageView();
        Router.events.on('routeChangeComplete', () => {
          ReactPixel.pageView();
          // if (url.indexOf('Confirmed') > 0) {
          //   const split = url.split('/');
          //   ReactPixel.track('Purchase', { code: split[split.length - 1] });
          // }
        });
      });
  });
  return null;
}

export function reportWebVitals(metric: { label: string }) {
  if (metric.label === 'custom') {
    ;
  }
}

const DefaultLayout2 = ({ children, airports }) => {
  useEffect(() => {
    setLastPageVisited();
  });

  return (
    <Layout>
      <Head>
        <meta charSet="utf-8" key="charset" />
        <meta
          name="description"
          content="Save on airport parking with our low-price guarantee. Easy online reservations with free cancellation included."
        />
        <title>
          Reserve Airport Parking at the Lowest Rates | On Air Parking
        </title>
        {/* SEO */}
        <link rel="canonical" href="https://www.onairparking.com/" />
        <meta
          property="og:url"
          content={!isServer ? window.location.toString() : undefined}
          key="url"
        />
        <meta property="og:type" content="website" key="og:type" />
        <meta property="og:title" content="Reserve Airport Parking at the Lowest Rates | On Air Parking" />
        <meta property="og:description" content="Save on airport parking with our low-price guarantee. Easy online reservations with free cancellation included." />
        <meta property="og:image" content={CDN2() + '/staticmyapp/oglogo-white.png'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Reserve Airport Parking at the Lowest Rates | On Air Parking" />
        <meta name="twitter:description" content="Save on airport parking with our low-price guarantee. Easy online reservations with free cancellation included." />
        <meta name="twitter:image" content={CDN2() + '/staticmyapp/oglogo-white.png'} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@graph": [
                {
                  "@type": "Organization",
                  "@id": "https://www.onairparking.com/#organization",
                  "name": "On Air Parking",
                  "alternateName": "OnAirParking",
                  "url": "https://www.onairparking.com/",
                  "logo": {
                    "@type": "ImageObject",
                    "@id": "https://www.onairparking.com/#logo",
                    "url": CDN2() + '/staticmyapp/oglogo-white.png',
                    "width": 1024,
                    "height": 1024
                  },
                  "description": "On Air Parking is an airport parking reservation platform that helps travelers compare and reserve affordable parking near airports across the United States and Canada.",
                  "address": {
                    "@type": "PostalAddress",
                    "streetAddress": "1014 S. Westlake Blvd, Suite 14-333",
                    "addressLocality": "Westlake Village",
                    "addressRegion": "CA",
                    "postalCode": "91361",
                    "addressCountry": "US"
                  },
                  "contactPoint": [
                    {
                      "@type": "ContactPoint",
                      "contactType": "customer service",
                      "telephone": "+1-888-487-2754",
                      "email": "hello@noson.io",
                      "areaServed": [
                        {
                          "@type": "Country",
                          "name": "United States"
                        },
                        {
                          "@type": "Country",
                          "name": "Canada"
                        }
                      ],
                      "availableLanguage": ["English"]
                    },
                    {
                      "@type": "ContactPoint",
                      "contactType": "text support",
                      "telephone": "+1-424-532-8940",
                      "areaServed": [
                        {
                          "@type": "Country",
                          "name": "United States"
                        },
                        {
                          "@type": "Country",
                          "name": "Canada"
                        }
                      ],
                      "availableLanguage": ["English"]
                    }
                  ],
                  "sameAs": [
                    "https://www.youtube.com/@onairparking",
                    "https://www.instagram.com/onairparking/",
                    "https://www.facebook.com/ParkOnAir",
                    "https://www.linkedin.com/company/onairparking/",
                    "https://www.tiktok.com/@onairparking",
                    "https://x.com/onairparking"
                  ],
                  "areaServed": [
                    {
                      "@type": "Country",
                      "name": "United States"
                    },
                    {
                      "@type": "Country",
                      "name": "Canada"
                    }
                  ]
                },
                {
                  "@type": "WebSite",
                  "@id": "https://www.onairparking.com/#website",
                  "url": "https://www.onairparking.com/",
                  "name": "On Air Parking",
                  "alternateName": "OnAirParking",
                  "inLanguage": "en",
                  "publisher": {
                    "@id": "https://www.onairparking.com/#organization"
                  },
                  "potentialAction": {
                    "@type": "SearchAction",
                    "target": {
                      "@type": "EntryPoint",
                      "urlTemplate": "https://www.onairparking.com/reservation/search?checkin={checkin_datetime}&checkout={checkout_datetime}&airport={airport_name}&initials={airport_code}"
                    }
                  }
                },
                {
                  "@type": "WebPage",
                  "@id": "https://www.onairparking.com/#webpage",
                  "url": "https://www.onairparking.com/",
                  "name": "Reserve Airport Parking at the Lowest Rates | On Air Parking",
                  "description": "Save on airport parking with our low-price guarantee. Easy online reservations with free cancellation included.",
                  "isPartOf": {
                    "@id": "https://www.onairparking.com/#website"
                  },
                  "about": {
                    "@id": "https://www.onairparking.com/#organization"
                  },
                  "primaryImageOfPage": {
                    "@id": "https://www.onairparking.com/#logo"
                  }
                },
                {
                  "@type": "Service",
                  "@id": "https://www.onairparking.com/#service",
                  "name": "Airport Parking Reservation Service",
                  "serviceType": "Airport parking reservation platform",
                  "provider": {
                    "@id": "https://www.onairparking.com/#organization"
                  },
                  "url": "https://www.onairparking.com/",
                  "description": "Compare and reserve airport parking near airports across the United States and Canada.",
                  "areaServed": [
                    {
                      "@type": "Country",
                      "name": "United States"
                    },
                    {
                      "@type": "Country",
                      "name": "Canada"
                    }
                  ]
                }
              ]
            }
            )
          }}
        />
        {/* <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "WebSite",
              "@id": "https://www.onairparking.com/#website",
              "url": "https://www.onairparking.com/",
              "name": "On Air Parking",
              "inLanguage": "en",
              "publisher": { "@id": "https://www.onairparking.com/#org" },
              "potentialAction": {
                "@type": "SearchAction",
                "target": "https://www.google.com/search?q=site:onairparking.com+{search_term_string}",
                "query-input": "required name=search_term_string"
              }
            }
            )
          }}
        /> */}

      </Head>
      {isProdution && <FacebookPixel />}
      <NavBar airports={airports} isHomepage={true} />
      <Content className="site-layout">{children}</Content>
      <AntFooter />
    </Layout>
  );
};

export default observer(DefaultLayout2);
